import {
  DEFAULT_QUOTE,
  FIAT_CODES,
  priceInclude,
  type PriceCatalog,
} from "./price-catalog";

/** Ledger excerpts are parsed locally; availability comes from the live catalog. */
/** Metadata literals that share the commodity spelling but are not commodities. */
const NOT_COMMODITIES: ReadonlySet<string> = new Set(["TRUE", "FALSE", "NULL"]);

/**
 * A Beancount commodity: an uppercase letter, then up to 22 of
 * `A-Z 0-9 ' . _ -`, ending in a letter or digit. A token preceded by `:` is an
 * account component (`Assets:BTC`), not a commodity, and one preceded by a
 * word character is the tail of a longer word.
 */
const COMMODITY_TOKEN =
  /(?<![\w:'.-])([A-Z][A-Z0-9'._-]{0,22}[A-Z0-9]|[A-Z])(?![\w'.-])/g;
const OPERATING_CURRENCY = /^\s*option\s+"operating_currency"\s+"([^"]+)"/gm;
const COMMENT = /;[^\n]*/g;
const QUOTED_STRING = /"(?:[^"\\]|\\.)*"/g;

type PairStatus = "available" | "unknown" | "delisted";

type PricePair = {
  base: string;
  quote: string;
  alias: string;
  include: string | null;
  assetId: string | null;
  status: PairStatus;
};

type PairInference = {
  /** Every commodity the excerpt mentions, in first-seen order. */
  commodities: string[];
  /** The quote currencies pairs are valued in. */
  quotes: string[];
  /** Whether `quotes` came from `option "operating_currency"` lines. */
  declaredQuotes: boolean;
  /** Base × quote, available aliases first, then alphabetical. */
  pairs: PricePair[];
};

function unique(values: Iterable<string>): string[] {
  return [...new Set(values)];
}

/** Commodities mentioned in an excerpt, in first-seen order. */
export function extractCommodities(text: string): string[] {
  const stripped = text.replace(COMMENT, " ").replace(QUOTED_STRING, " ");
  const found: string[] = [];
  for (const match of stripped.matchAll(COMMODITY_TOKEN)) {
    const token = match[1];
    if (!NOT_COMMODITIES.has(token)) found.push(token);
  }
  return unique(found);
}

/** Operating currencies declared with `option "operating_currency"`. */
export function extractOperatingCurrencies(text: string): string[] {
  return unique(
    [...text.matchAll(OPERATING_CURRENCY)]
      .map((match) => match[1].trim())
      .filter((code) => code.length > 0),
  );
}

export function inferPricePairs(
  text: string,
  catalog: PriceCatalog,
  quoteOverride?: string,
): PairInference {
  const commodities = extractCommodities(text);
  const declared = extractOperatingCurrencies(text);
  const declaredQuotes = declared.length > 0;
  const quotes = declaredQuotes
    ? declared
    : commodities.filter((code) => FIAT_CODES.has(code));
  const effectiveQuotes = quoteOverride
    ? [quoteOverride]
    : quotes.length > 0
      ? quotes
      : [DEFAULT_QUOTE];
  const quoteSet = new Set(effectiveQuotes);
  const bases = unique([...declared, ...commodities]).filter(
    (code) => !quoteSet.has(code),
  );

  const pairs: PricePair[] = [];
  for (const base of bases) {
    for (const quote of effectiveQuotes) {
      const alias = `${base}-${quote}`;
      const asset = catalog.byAlias.get(alias);
      const status: PairStatus = catalog.delisted.has(alias)
        ? "delisted"
        : asset
          ? "available"
          : "unknown";
      pairs.push({
        base,
        quote,
        alias,
        include: status === "available" ? priceInclude(base, quote) : null,
        assetId: asset?.id ?? null,
        status,
      });
    }
  }
  pairs.sort((a, b) => {
    if ((a.status === "available") !== (b.status === "available"))
      return a.status === "available" ? -1 : 1;
    return a.alias.localeCompare(b.alias);
  });

  return {
    commodities: unique([...declared, ...commodities]),
    quotes: effectiveQuotes,
    declaredQuotes,
    pairs,
  };
}

/** The include lines for every pair the hosted feed serves, one per line. */
export function availableIncludes(pairs: readonly PricePair[]): string {
  return pairs
    .filter((pair) => pair.status === "available")
    .map((pair) => pair.include)
    .join("\n");
}

/** A short excerpt that shows the finder working before the visitor pastes. */
export const SAMPLE_LEDGER = `option "operating_currency" "USD"

2024-01-05 * "Coinbase" "Buy bitcoin"
  Assets:Crypto:Coinbase     0.5 BTC {60000 USD}
  Assets:Bank:Checking    -30000 USD

2024-03-12 * "Broker" "Buy shares"
  Assets:Broker:Schwab        10 AAPL {170 USD}
  Assets:Bank:Checking     -1700 USD

2024-06-01 * "Kraken" "Buy ether"
  Assets:Crypto:Kraken         2 ETH {3000 USD}
  Assets:Bank:Checking     -6000 USD
`;
